/**
 * PreciosVigencia — validación semanal de los precios con que se calcula el costo.
 *
 * Al entrar a la página:
 *   - si los precios llevan más de una semana sin validarse (o nunca se validaron),
 *     se validan solos con los proveedores y se muestra el avance;
 *   - si no, se muestra la fecha de la última validación y un botón para
 *     actualizarlos cuando se quiera.
 *
 * Quien decide de verdad si toca es el servidor (modo «auto» no hace nada si la
 * validación es reciente), así que abrir la página varias veces, o dos
 * administradores a la vez, no repite el trabajo.
 *
 * Los precios que no se pudieron validar (a revisar, sin datos, con error) se
 * muestran en un aviso visible: un precio desactualizado distorsiona todo el costo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { AlertTriangle, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import dashStyles from '../../pages/admin/AdminDashboardPage.module.css';
import { functions } from '../../config/firebase';
import { useDocument } from '../../hooks/useFirestore';
import { useToast } from '../../contexts/ToastContext';
import {
  DIAS_VIGENCIA_PRECIOS, ESTADO_PRECIO_LABELS, PANAMA_TZ, aFecha, fmtPrecio, preciosVencidos,
  type CambioPrecioDoc, type PreciosEstadoDoc, type ResultadoPrecioDoc, type ResumenPreciosDoc,
} from './costosUtil';
import styles from './PreciosVigencia.module.css';

interface RespuestaActualizar {
  ejecutado: boolean;
  motivo?: 'vigente' | 'en_curso' | 'reintento_pronto';
  resumen?: ResumenPreciosDoc;
}

const fechaLarga = (d: Date) =>
  d.toLocaleString('es-PA', { timeZone: PANAMA_TZ, day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit' });

function haceCuanto(d: Date, ahora = Date.now()): string {
  const dias = Math.floor((ahora - d.getTime()) / 86_400_000);
  if (dias <= 0) return 'hoy';
  return dias === 1 ? 'hace 1 día' : `hace ${dias} días`;
}

function diasHastaProxima(d: Date, ahora = Date.now()): number {
  return Math.max(0, Math.ceil(DIAS_VIGENCIA_PRECIOS - (ahora - d.getTime()) / 86_400_000));
}

const claseEstado = (e: ResultadoPrecioDoc['estado']) => styles[`estado_${e}`];

export default function PreciosVigencia() {
  const { addToast } = useToast();
  const estado = useDocument<PreciosEstadoDoc>('costos_config', 'precios_estado');
  const historial = useDocument<{ cambios?: CambioPrecioDoc[] }>('costos_config', 'precios_historial');
  const [ejecutando, setEjecutando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);
  const yaLanzado = useRef(false);

  const ejecutar = useCallback(async (modo: 'auto' | 'manual') => {
    setEjecutando(true);
    setFalla(null);
    try {
      const res = await httpsCallable<{ modo: string }, RespuestaActualizar>(
        functions, 'portal_costosActualizarPrecios', { timeout: 150_000 },
      )({ modo });
      const r = res.data;
      if (r.ejecutado && r.resumen) {
        const { actualizados, revisar, errores, sinDatos } = r.resumen;
        const pendientes = revisar + errores + sinDatos;
        if (pendientes > 0) {
          addToast('warning', `Precios validados: ${actualizados} actualizado(s), pero ${pendientes} no se pudieron confirmar. Revise el aviso.`);
        } else if (actualizados > 0) {
          addToast('success', `Precios validados: se actualizaron ${actualizados}.`);
        } else if (modo === 'manual') {
          addToast('success', 'Precios validados: todos estaban al día.');
        }
      } else if (modo === 'manual' && r.motivo === 'en_curso') {
        addToast('info', 'Ya hay una validación de precios en curso. Espere unos segundos.');
      }
    } catch (err) {
      console.error('[PreciosVigencia] portal_costosActualizarPrecios', err);
      setFalla('No se pudieron validar los precios. Los valores guardados siguen en uso; intente de nuevo en unos minutos.');
    } finally {
      setEjecutando(false);
    }
  }, [addToast]);

  const ultima = aFecha(estado.data?.ultimaActualizacion);
  const vencida = preciosVencidos(ultima);

  // Una sola vez por visita, cuando ya se sabe cuándo fue la última validación.
  useEffect(() => {
    if (estado.loading || estado.error || yaLanzado.current) return;
    yaLanzado.current = true;
    if (vencida) void ejecutar('auto');
  }, [estado.loading, estado.error, vencida, ejecutar]);

  if (estado.error) {
    console.warn('[PreciosVigencia] no se pudo leer costos_config/precios_estado', estado.error);
    return null;
  }

  const enCursoOtro = !ejecutando && !!estado.data?.enCurso;
  const ocupado = ejecutando || enCursoOtro;
  const resumen = estado.data?.resumen;
  const resultados = estado.data?.resultados ?? [];
  const pendientes = resultados.filter((r) => r.estado === 'revisar' || r.estado === 'sin_datos' || r.estado === 'error');
  const hayError = pendientes.some((r) => r.estado === 'error');
  const cambios = (historial.data?.cambios ?? []).filter((c) => c.anterior !== c.nuevo).slice(0, 8);

  return (
    <section className={styles.caja} aria-label="Validación de precios">
      <div className={styles.fila}>
        {ocupado ? (
          <p className={styles.validando} role="status">
            <Loader2 size={16} className={dashStyles.spinning} aria-hidden="true" />
            Validando los precios con los proveedores… puede tardar unos segundos.
          </p>
        ) : estado.loading ? (
          <p className={styles.sub}>Revisando la vigencia de los precios…</p>
        ) : (
          <div className={styles.texto}>
            <p className={styles.titulo}>
              <ShieldCheck size={14} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 6 }} />
              {ultima
                ? <>Precios validados el {fechaLarga(ultima)} ({haceCuanto(ultima)})</>
                : 'Los precios todavía no se han validado'}
            </p>
            <p className={styles.sub}>
              {ultima
                ? <>Se revisan solos al entrar a esta página cuando pasa más de una semana. Próxima validación automática en {diasHastaProxima(ultima)} {diasHastaProxima(ultima) === 1 ? 'día' : 'días'}.</>
                : 'Se validarán al abrir esta página.'}
            </p>
          </div>
        )}
        <button type="button" className={styles.boton} onClick={() => void ejecutar('manual')} disabled={ocupado || estado.loading}>
          <RefreshCw size={14} aria-hidden="true" /> Actualizar precios
        </button>
      </div>

      {falla && (
        <div className={`${styles.aviso} ${styles.avisoError}`} role="alert">
          <AlertTriangle size={18} aria-hidden="true" /><p>{falla}</p>
        </div>
      )}

      {!ocupado && pendientes.length > 0 && (
        <div className={`${styles.aviso} ${hayError ? styles.avisoError : ''}`} role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <p>
            <strong>{pendientes.length === 1 ? '1 precio no se pudo confirmar' : `${pendientes.length} precios no se pudieron confirmar`}.</strong>{' '}
            Mientras tanto se sigue usando el valor guardado, que puede estar desactualizado. Vea el detalle abajo.
          </p>
        </div>
      )}

      {!ocupado && resumen && (
        <ul className={styles.chips} aria-label="Resultado de la última validación">
          <li className={`${styles.chip} ${styles.chipOk}`}>{resumen.actualizados} actualizados</li>
          <li className={styles.chip}>{resumen.sinCambio} sin cambio</li>
          {resumen.revisar > 0 && <li className={`${styles.chip} ${styles.chipAviso}`}>{resumen.revisar} a revisar</li>}
          {resumen.sinDatos > 0 && <li className={`${styles.chip} ${styles.chipAviso}`}>{resumen.sinDatos} sin datos</li>}
          {resumen.errores > 0 && <li className={`${styles.chip} ${styles.chipError}`}>{resumen.errores} con error</li>}
          {resumen.manuales > 0 && <li className={styles.chip}>{resumen.manuales} manuales</li>}
        </ul>
      )}

      {!ocupado && resultados.length > 0 && (
        <details className={styles.detalle}>
          <summary>Ver el detalle de los precios</summary>
          <div className={styles.scroll}>
            <table className={styles.tabla}>
              <thead>
                <tr><th>Precio</th><th>Fuente</th><th className={styles.num}>Anterior</th><th className={styles.num}>Actual</th><th>Estado</th></tr>
              </thead>
              <tbody>
                {[...resultados]
                  .sort((a, b) => Number(['revisar', 'error', 'sin_datos'].includes(b.estado)) - Number(['revisar', 'error', 'sin_datos'].includes(a.estado)))
                  .map((r) => (
                    <tr key={r.clave}>
                      <td>
                        {r.nombre}
                        <span className={styles.nota}>{r.unidad}</span>
                        {r.nota && <span className={styles.nota}>{r.nota}</span>}
                      </td>
                      <td>{r.fuente}</td>
                      <td className={styles.num}>{fmtPrecio(r.anterior)}</td>
                      <td className={styles.num}>{fmtPrecio(r.nuevo ?? r.anterior)}</td>
                      <td className={`${styles.estado} ${claseEstado(r.estado)}`}>{ESTADO_PRECIO_LABELS[r.estado]}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {cambios.length > 0 && (
            <>
              <p className={styles.sub} style={{ marginTop: 'var(--space-3)' }}><strong>Últimos cambios aplicados</strong></p>
              <div className={styles.scroll}>
                <table className={styles.tabla}>
                  <thead><tr><th>Cuándo</th><th>Precio</th><th className={styles.num}>Antes</th><th className={styles.num}>Ahora</th></tr></thead>
                  <tbody>
                    {cambios.map((c) => (
                      <tr key={`${c.en}-${c.clave}`}>
                        <td>{new Date(c.en).toLocaleDateString('es-PA', { timeZone: PANAMA_TZ, day: 'numeric', month: 'short' })}</td>
                        <td>{c.nombre}<span className={styles.nota}>{c.unidad}</span></td>
                        <td className={styles.num}>{fmtPrecio(c.anterior)}</td>
                        <td className={styles.num}>{fmtPrecio(c.nuevo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </details>
      )}
    </section>
  );
}
